// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { newEntityId } from '../util/jobLifecycle';

const STORAGE_KEY = 'bx_reputation';

const readStored = () => {
  if (typeof window === 'undefined') {
    return { reviews: [], profiles: {} };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { reviews: [], profiles: {} };
    }
    const parsed = JSON.parse(raw);
    return {
      reviews: Array.isArray(parsed.reviews) ? parsed.reviews : [],
      profiles: parsed.profiles && typeof parsed.profiles === 'object' ? parsed.profiles : {},
    };
  } catch {
    return { reviews: [], profiles: {} };
  }
};

const writeStored = state => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const avg = reviews => {
  if (!reviews.length) {
    return null;
  }
  const sum = reviews.reduce((a, r) => a + Number(r.rating || 0), 0);
  return Math.round((sum / reviews.length) * 10) / 10;
};

const ReputationContext = createContext({
  reviews: [],
  profiles: {},
  getProviderStats: () => ({ avgRating: null, count: 0, badges: [] }),
  hasReviewedJob: () => false,
  submitReview: () => {},
  ensureProfile: () => {},
});

export const ReputationProvider = ({ children }) => {
  const [state, setState] = useState(readStored);

  useEffect(() => {
    writeStored(state);
  }, [state]);

  const ensureProfile = useCallback((providerId, seed = {}) => {
    if (!providerId) {
      return;
    }
    setState(prev => {
      const existing = prev.profiles[providerId];
      const nextProfile = {
        phoneVerified: Boolean(
          seed.phoneVerified ?? seed.verifiedPhone ?? existing?.phoneVerified
        ),
        completionCount: Number(
          seed.completionCount ?? existing?.completionCount ?? seed.years ?? 0
        ),
        displayName: seed.name || seed.displayName || existing?.displayName || providerId,
      };
      if (
        existing &&
        existing.phoneVerified === nextProfile.phoneVerified &&
        existing.completionCount === nextProfile.completionCount &&
        existing.displayName === nextProfile.displayName
      ) {
        return prev;
      }
      return {
        ...prev,
        profiles: {
          ...prev.profiles,
          [providerId]: nextProfile,
        },
      };
    });
  }, []);

  const getProviderStats = useCallback(
    providerId => {
      const list = state.reviews.filter(
        r => r.providerId === providerId && r.direction === 'buyer_to_provider'
      );
      const profile = state.profiles[providerId] || {};
      const badges = [];
      if (profile.phoneVerified) {
        badges.push('Phone verified');
      }
      const completions = Number(profile.completionCount || 0) + list.filter(r => r.rating >= 4).length;
      if (completions >= 5) {
        badges.push(`${completions}+ jobs`);
      } else if (completions >= 1) {
        badges.push('Active');
      }
      if (avg(list) != null && avg(list) >= 4.5 && list.length >= 2) {
        badges.push('Top rated');
      }
      return {
        avgRating: avg(list),
        count: list.length,
        badges,
        phoneVerified: Boolean(profile.phoneVerified),
        completionCount: completions,
      };
    },
    [state]
  );

  const hasReviewedJob = useCallback(
    (jobId, direction = 'buyer_to_provider') =>
      state.reviews.some(r => r.jobId === jobId && r.direction === direction),
    [state.reviews]
  );

  const submitReview = useCallback(
    ({
      jobId,
      providerId,
      providerName,
      rating,
      comment = '',
      direction = 'buyer_to_provider',
    }) => {
      const stars = Math.min(5, Math.max(1, Number(rating) || 0));
      if (!jobId || !providerId || !stars) {
        return;
      }
      setState(prev => {
        if (prev.reviews.some(r => r.jobId === jobId && r.direction === direction)) {
          return prev;
        }
        const review = {
          id: newEntityId('rev'),
          jobId,
          providerId,
          providerName,
          rating: stars,
          comment: String(comment || '').trim(),
          direction,
          createdAt: new Date().toISOString(),
        };
        const profiles = { ...prev.profiles };
        if (!profiles[providerId]) {
          profiles[providerId] = {
            phoneVerified: false,
            completionCount: 0,
            displayName: providerName || providerId,
          };
        }
        if (direction === 'buyer_to_provider' && stars >= 4) {
          profiles[providerId] = {
            ...profiles[providerId],
            completionCount: Number(profiles[providerId].completionCount || 0) + 1,
          };
        }
        return {
          reviews: [review, ...prev.reviews],
          profiles,
        };
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      reviews: state.reviews,
      profiles: state.profiles,
      getProviderStats,
      hasReviewedJob,
      submitReview,
      ensureProfile,
    }),
    [state, getProviderStats, hasReviewedJob, submitReview, ensureProfile]
  );

  return <ReputationContext.Provider value={value}>{children}</ReputationContext.Provider>;
};

export const useReputation = () => useContext(ReputationContext);

export default ReputationContext;
